import { __awaiter, __generator, __read, __spreadArray, __values } from "tslib";
import { SchemaDiffUtils, TransactionMode, KeyGenerator, MIGRATION_ASSIST_TABLE_SCHEMA, } from '@tencent/memoire-core';
import { NoTableInfoError, TransactionCommitError } from '../error';
import { TableOperationUtils } from '../model';
import { DATABASE_DEFAULT_CURRENT_VERSION } from './constant';
var ALLOY_DB_SYSTEM_TABLE_NAME = MIGRATION_ASSIST_TABLE_SCHEMA.name;
export var DB_SCHEMA_FROM;
(function (DB_SCHEMA_FROM) {
    DB_SCHEMA_FROM["SYSTEM_TABLE"] = "system_table";
    DB_SCHEMA_FROM["SCAN_EXISTING_TABLE"] = "scan_exists_table";
})(DB_SCHEMA_FROM || (DB_SCHEMA_FROM = {}));
/**
 * The system table component is designed to assist the migration logic.
 * The table records the schema structure and data version corresponding to the business registered Model.
 * When the database is upgraded, we compare the difference between the schema corresponding to
 * the system table and the registered Model. So we can infer the required migration items;
 */
var SystemTableHelper = /** @class */ (function () {
    function SystemTableHelper() {
    }
    SystemTableHelper.hasSystemTable = function (db) {
        return db.tableNames.includes(ALLOY_DB_SYSTEM_TABLE_NAME);
    };
    /**
     * Obtain the actual database table structure of the database through the component-defined system table.
     * If the table is missing, it will return null.
     * In this case, the database may be tampered with or damaged.
     * It is recommended to clean the data before using it to avoid secondary errors.
     * @param dbName
     * @param engine
     * @returns the schema of actual database or null if system table missing
     */
    SystemTableHelper.getDatabaseSchema = function (dbName, engine, expectedSchema) {
        return __awaiter(this, void 0, void 0, function () {
            var db, dbClosed, schema_1, transaction, systemTable, tableSchemas, schema;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, engine.openDB(dbName, {
                            name: dbName,
                            collections: [MIGRATION_ASSIST_TABLE_SCHEMA],
                        })];
                    case 1:
                        db = _a.sent();
                        dbClosed = false;
                        _a.label = 2;
                    case 2:
                        _a.trys.push([2, , 10, 11]);
                        if (!!db.tableNames.length) return [3 /*break*/, 4];
                        // delete db will wait for database connection close, so we need close before
                        db.close();
                        dbClosed = true;
                        return [4 /*yield*/, engine.deleteDB(dbName)];
                    case 3:
                        _a.sent();
                        return [2 /*return*/, {
                                schema: {
                                    name: this.name,
                                    version: DATABASE_DEFAULT_CURRENT_VERSION,
                                    collections: [],
                                },
                                from: "scan_exists_table" /* SCAN_EXISTING_TABLE */,
                            }];
                    case 4:
                        if (!!this.hasSystemTable(db)) return [3 /*break*/, 6];
                        return [4 /*yield*/, SystemTableHelper.getDatabaseSchemaByScanningTableStructure(expectedSchema.collections.map(function (schema) { return schema.name; }), db)];
                    case 5:
                        schema_1 = _a.sent();
                        return [2 /*return*/, {
                                schema: schema_1,
                                from: "scan_exists_table" /* SCAN_EXISTING_TABLE */,
                            }];
                    case 6: return [4 /*yield*/, db.transaction([ALLOY_DB_SYSTEM_TABLE_NAME], TransactionMode.READ_ONLY)];
                    case 7:
                        transaction = _a.sent();
                        systemTable = transaction.table(ALLOY_DB_SYSTEM_TABLE_NAME);
                        return [4 /*yield*/, systemTable.getAll()];
                    case 8:
                        tableSchemas = (_a.sent()) || [];
                        schema = {
                            name: this.name,
                            version: db.version,
                            collections: __spreadArray(__spreadArray([], __read(tableSchemas)), [this.getSystemTableSchema()]),
                        };
                        return [4 /*yield*/, transaction.commit()];
                    case 9:
                        _a.sent();
                        return [2 /*return*/, { schema: schema, from: "system_table" /* SYSTEM_TABLE */ }];
                    case 10:
                        if (!dbClosed) {
                            db.close();
                        }
                        return [7 /*endfinally*/];
                    case 11: return [2 /*return*/];
                }
            });
        });
    };
    SystemTableHelper.getSystemTableSchema = function () {
        return MIGRATION_ASSIST_TABLE_SCHEMA;
    };
    /**
     * sync model schema change to system table
     * @param version current version
     * @param db database
     * @param state databases state
     */
    SystemTableHelper.updateSchemaChange = function (version, transaction, state, systemTableFirstCreate) {
        return __awaiter(this, void 0, void 0, function () {
            var table, diff, addedTableSchemas, addedTableSchemas_1, addedTableSchemas_1_1, tableSchema, e_1_1, _a, _b, tableSchema, e_2_1, indexChangedSchema, _loop_1, indexChangedSchema_1, indexChangedSchema_1_1, changedSchema, e_3_1, ret, err_1;
            var e_1, _c, e_2, _d, e_3, _e;
            return __generator(this, function (_f) {
                switch (_f.label) {
                    case 0:
                        table = transaction.table(ALLOY_DB_SYSTEM_TABLE_NAME);
                        if (!systemTableFirstCreate) return [3 /*break*/, 2];
                        return [4 /*yield*/, table.bulkUpsert(state.schema.expect.collections.filter(function (tableSchema) { return tableSchema.name !== ALLOY_DB_SYSTEM_TABLE_NAME; }))];
                    case 1:
                        _f.sent();
                        return [2 /*return*/];
                    case 2:
                        _f.trys.push([2, 27, , 29]);
                        diff = SchemaDiffUtils.databaseDiff(state.schema.actual, state.schema.expect);
                        addedTableSchemas = diff.added.filter(function (col) { return col.name !== ALLOY_DB_SYSTEM_TABLE_NAME; });
                        _f.label = 3;
                    case 3:
                        _f.trys.push([3, 8, 9, 10]);
                        addedTableSchemas_1 = __values(addedTableSchemas), addedTableSchemas_1_1 = addedTableSchemas_1.next();
                        _f.label = 4;
                    case 4:
                        if (!!addedTableSchemas_1_1.done) return [3 /*break*/, 7];
                        tableSchema = addedTableSchemas_1_1.value;
                        tableSchema.dataVersion = version;
                        return [4 /*yield*/, table.upsert(tableSchema)];
                    case 5:
                        _f.sent();
                        _f.label = 6;
                    case 6:
                        addedTableSchemas_1_1 = addedTableSchemas_1.next();
                        return [3 /*break*/, 4];
                    case 7: return [3 /*break*/, 10];
                    case 8:
                        e_1_1 = _f.sent();
                        e_1 = { error: e_1_1 };
                        return [3 /*break*/, 10];
                    case 9:
                        try {
                            if (addedTableSchemas_1_1 && !addedTableSchemas_1_1.done && (_c = addedTableSchemas_1.return)) _c.call(addedTableSchemas_1);
                        }
                        finally { if (e_1) throw e_1.error; }
                        return [7 /*endfinally*/];
                    case 10:
                        _f.trys.push([10, 15, 16, 17]);
                        _a = __values(diff.deleted), _b = _a.next();
                        _f.label = 11;
                    case 11:
                        if (!!_b.done) return [3 /*break*/, 14];
                        tableSchema = _b.value;
                        return [4 /*yield*/, table.delete(tableSchema.name)];
                    case 12:
                        _f.sent();
                        _f.label = 13;
                    case 13:
                        _b = _a.next();
                        return [3 /*break*/, 11];
                    case 14: return [3 /*break*/, 17];
                    case 15:
                        e_2_1 = _f.sent();
                        e_2 = { error: e_2_1 };
                        return [3 /*break*/, 17];
                    case 16:
                        try {
                            if (_b && !_b.done && (_d = _a.return)) _d.call(_a);
                        }
                        finally { if (e_2) throw e_2.error; }
                        return [7 /*endfinally*/];
                    case 17:
                        indexChangedSchema = diff.different.filter(function (col) { return !!col.index; });
                        _loop_1 = function (changedSchema) {
                            var oldSchema, newIndices, existedIndices;
                            return __generator(this, function (_g) {
                                switch (_g.label) {
                                    case 0: return [4 /*yield*/, table.get(changedSchema.collection)];
                                    case 1:
                                        oldSchema = _g.sent();
                                        newIndices = [];
                                        existedIndices = oldSchema.indices.filter(function (index) {
                                            return !changedSchema.index.deleted.some(function (delIndex) { return delIndex.name === index.name; });
                                        });
                                        // push new indices
                                        newIndices.push.apply(newIndices, __spreadArray([], __read(changedSchema.index.added)));
                                        // push exist indices
                                        newIndices.push.apply(newIndices, __spreadArray([], __read(existedIndices)));
                                        oldSchema.index = newIndices;
                                        return [4 /*yield*/, table.upsert(oldSchema)];
                                    case 2:
                                        _g.sent();
                                        return [2 /*return*/];
                                }
                            });
                        };
                        _f.label = 18;
                    case 18:
                        _f.trys.push([18, 23, 24, 25]);
                        indexChangedSchema_1 = __values(indexChangedSchema), indexChangedSchema_1_1 = indexChangedSchema_1.next();
                        _f.label = 19;
                    case 19:
                        if (!!indexChangedSchema_1_1.done) return [3 /*break*/, 22];
                        changedSchema = indexChangedSchema_1_1.value;
                        return [5 /*yield**/, _loop_1(changedSchema)];
                    case 20:
                        _f.sent();
                        _f.label = 21;
                    case 21:
                        indexChangedSchema_1_1 = indexChangedSchema_1.next();
                        return [3 /*break*/, 19];
                    case 22: return [3 /*break*/, 25];
                    case 23:
                        e_3_1 = _f.sent();
                        e_3 = { error: e_3_1 };
                        return [3 /*break*/, 25];
                    case 24:
                        try {
                            if (indexChangedSchema_1_1 && !indexChangedSchema_1_1.done && (_e = indexChangedSchema_1.return)) _e.call(indexChangedSchema_1);
                        }
                        finally { if (e_3) throw e_3.error; }
                        return [7 /*endfinally*/];
                    case 25: return [4 /*yield*/, transaction.commit()];
                    case 26:
                        ret = _f.sent();
                        if (!ret) {
                            throw new TransactionCommitError();
                        }
                        return [3 /*break*/, 29];
                    case 27:
                        err_1 = _f.sent();
                        return [4 /*yield*/, transaction.rollback()];
                    case 28:
                        _f.sent();
                        throw err_1;
                    case 29: return [2 /*return*/];
                }
            });
        });
    };
    /**
     * Update the data version number in the system table. If the update is successful, it will return true.
     * At present, except for the fact that there are other tabs that cannot be updated due to data upgrades
     * in the case of multiple tabs, it will return false. Other errors will be exposed by throwing Error.
     */
    SystemTableHelper.updateCollectionDataVersion = function (version, transaction, modelClass) {
        return __awaiter(this, void 0, void 0, function () {
            var table, colSchema, tableInfo;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        table = transaction.table(ALLOY_DB_SYSTEM_TABLE_NAME);
                        return [4 /*yield*/, table.get(TableOperationUtils.getTableName(modelClass))];
                    case 1:
                        colSchema = _a.sent();
                        tableInfo = TableOperationUtils.getTableInfo(modelClass.prototype);
                        if (!colSchema) {
                            throw new Error('get target table system info error!');
                        }
                        if (!tableInfo) {
                            throw new NoTableInfoError(modelClass.name);
                        }
                        colSchema.dataVersion = version;
                        colSchema.properties = __spreadArray([], __read(tableInfo.properties.values())).map(function (prop) { return prop.name; });
                        return [4 /*yield*/, table.upsert(colSchema)];
                    case 2:
                        _a.sent();
                        return [2 /*return*/];
                }
            });
        });
    };
    /**
     * get the table dataVersion from system table
     * @param tableName target table name
     * @param transaction transaction which include system table
     * @returns target table data version
     */
    SystemTableHelper.getTableDataVersion = function (tableName, transaction) {
        return __awaiter(this, void 0, void 0, function () {
            var table, colSchema;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        table = transaction.table(ALLOY_DB_SYSTEM_TABLE_NAME);
                        return [4 /*yield*/, table.get(tableName)];
                    case 1:
                        colSchema = _a.sent();
                        return [2 /*return*/, colSchema.dataVersion];
                }
            });
        });
    };
    /**
     * get database schema
     * @param tableName the name of the tables which is expected to get schema
     */
    SystemTableHelper.getDatabaseSchemaByScanningTableStructure = function (tableNames, db) {
        return __awaiter(this, void 0, void 0, function () {
            var dbExistTableNames, colSchemas, dbExistTableNames_1, dbExistTableNames_1_1, tableName, table, colSchema, _a, _b, indexName, index, e_4_1;
            var e_4, _c, e_5, _d;
            return __generator(this, function (_e) {
                switch (_e.label) {
                    case 0:
                        dbExistTableNames = db.tableNames.filter(function (tableName) { return tableNames.includes(tableName); });
                        colSchemas = [];
                        _e.label = 1;
                    case 1:
                        _e.trys.push([1, 6, 7, 8]);
                        dbExistTableNames_1 = __values(dbExistTableNames), dbExistTableNames_1_1 = dbExistTableNames_1.next();
                        _e.label = 2;
                    case 2:
                        if (!!dbExistTableNames_1_1.done) return [3 /*break*/, 5];
                        tableName = dbExistTableNames_1_1.value;
                        return [4 /*yield*/, db.table(tableName, TransactionMode.READ_ONLY)];
                    case 3:
                        table = _e.sent();
                        colSchema = {
                            name: tableName,
                            key: table.autoIncrement ? { generator: KeyGenerator.AutoIncrement } : { path: table.keyPath },
                            indices: [],
                            properties: [],
                        };
                        try {
                            for (_a = (e_5 = void 0, __values(table.indexNames)), _b = _a.next(); !_b.done; _b = _a.next()) {
                                indexName = _b.value;
                                index = table.index(indexName);
                                colSchema.indices.push({ name: indexName, path: index.keyPath, unique: index.unique });
                            }
                        }
                        catch (e_5_1) { e_5 = { error: e_5_1 }; }
                        finally {
                            try {
                                if (_b && !_b.done && (_d = _a.return)) _d.call(_a);
                            }
                            finally { if (e_5) throw e_5.error; }
                        }
                        colSchemas.push(colSchema);
                        _e.label = 4;
                    case 4:
                        dbExistTableNames_1_1 = dbExistTableNames_1.next();
                        return [3 /*break*/, 2];
                    case 5: return [3 /*break*/, 8];
                    case 6:
                        e_4_1 = _e.sent();
                        e_4 = { error: e_4_1 };
                        return [3 /*break*/, 8];
                    case 7:
                        try {
                            if (dbExistTableNames_1_1 && !dbExistTableNames_1_1.done && (_c = dbExistTableNames_1.return)) _c.call(dbExistTableNames_1);
                        }
                        finally { if (e_4) throw e_4.error; }
                        return [7 /*endfinally*/];
                    case 8: return [2 /*return*/, {
                            name: db.name,
                            version: db.version,
                            collections: colSchemas,
                        }];
                }
            });
        });
    };
    return SystemTableHelper;
}());
export { SystemTableHelper };
//# sourceMappingURL=system-table-helper.js.map